import React, { useState, useRef } from "react";
import { MODULES, MODULE_DESC, LINKS } from "../mock";
import { useReveal } from "../hooks/useReveal";
import { ArrowRight, Plus, Sparkles } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

function ModuleCard({ m, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="group relative rounded-2xl overflow-hidden glass card-lift h-full w-full text-left"
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={m.image}
          alt={m.color}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070d1c] to-transparent" />
        <span
          className="absolute top-3 left-3 text-[11px] font-semibold px-3 py-1 rounded-full text-[#10192c]"
          style={{ background: m.hex }}
        >
          Module {m.n}
        </span>
        <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-[#070d1c]/70 backdrop-blur flex items-center justify-center ring-1 ring-white/20 group-hover:bg-[#f6e3a8] group-hover:text-[#10192c] text-white transition-all">
          <Plus className="w-4 h-4" />
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.hex }} />
          <span className="text-xs uppercase tracking-widest text-white/55">
            {m.color} · {m.name}
          </span>
        </div>
        <h4 className="text-lg text-white leading-snug">{m.title}</h4>
        <span className="mt-3 inline-flex items-center gap-1 text-sm text-[#7fd8e8] group-hover:text-[#f6e3a8] transition-colors">
          En savoir plus <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </button>
  );
}

export default function Modules() {
  const head = useReveal();
  const [active, setActive] = useState(null);
  const autoplay = useRef(
    Autoplay({ delay: 2600, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  return (
    <section id="modules" className="relative py-24 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div ref={head} className="reveal text-center max-w-3xl mx-auto">
          <p className="eyebrow text-[#7fd8e8] text-xs mb-4">Phase 1 · 14 modules</p>
          <h2 className="text-4xl md:text-5xl text-white leading-tight">
            Tu te transformes une fréquence après l'autre <span className="text-gold">grâce à de puissants codes quantiques</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg">
            14 sessions, chacune une fréquence delphique. Clique sur un dauphin pour découvrir sa transformation.
          </p>
        </div>

        <div className="mt-14">
          <Carousel plugins={[autoplay.current]} opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {MODULES.map((m) => (
                <CarouselItem key={m.n} className="pl-4 basis-[80%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                  <ModuleCard m={m} onOpen={() => setActive(m)} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center gap-3 mt-8">
              <CarouselPrevious className="static translate-y-0 bg-white/5 border-white/15 text-white hover:bg-[#f6e3a8] hover:text-[#10192c]" />
              <CarouselNext className="static translate-y-0 bg-white/5 border-white/15 text-white hover:bg-[#f6e3a8] hover:text-[#10192c]" />
            </div>
          </Carousel>
        </div>
      </div>

      {/* Module detail popup */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg w-[92vw] bg-[#0a1224] border-white/10 p-0 overflow-hidden">
          {active && (
            <>
              <DialogTitle className="sr-only">{active.color} – {active.title}</DialogTitle>
              <div className="relative h-56">
                <img src={active.image} alt={active.color} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1224] via-[#0a1224]/30 to-transparent" />
                <span
                  className="absolute top-4 left-4 text-xs font-semibold px-3 py-1 rounded-full text-[#10192c]"
                  style={{ background: active.hex }}
                >
                  Module {active.n}
                </span>
              </div>
              <div className="p-6 -mt-8 relative">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: active.hex }} />
                  <span className="text-xs uppercase tracking-widest text-white/55">
                    {active.color} · {active.name}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl text-white leading-snug">{active.title}</h3>
                <p className="mt-4 text-white/70 leading-relaxed">{MODULE_DESC[active.n]}</p>

                <div className="mt-5 flex items-center gap-2 text-sm text-white/50">
                  <Sparkles className="w-4 h-4 text-[#f6e3a8]" />
                  Méditation · mantra · géométrie sacrée · exercices
                </div>

                <a
                  href={LINKS.inscription}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 btn-gold rounded-full px-6 py-3.5 font-semibold inline-flex items-center gap-2 group w-full justify-center"
                >
                  Je rejoins l'aventure
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
